import { Button } from '@tunasong/ui-lib'
import { useEffect, useState, type FC } from 'react'
import styles from './voice-leading-quiz-question.module.css'

export interface VoiceLeadingQuizQuestionPanelProps {
  scoreCorrect: number
  scoreAnswered: number
  /** Time from audition start to answer-button reveal — sequencer phaseBoundaries.endMs. */
  revealDelayMs: number
  onHearIt: () => void
  onAnswer: (slot: 'A' | 'B') => void
}

/**
 * Voice-Leading Quiz question panel. Per spec §5 the asking phase deliberately
 * hides the piano-rolls (they would leak the answer) — only an audition button.
 * The two answer buttons reveal once playback completes (`revealDelayMs`).
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §5
 */
export const VoiceLeadingQuizQuestionPanel: FC<VoiceLeadingQuizQuestionPanelProps> = props => {
  const { scoreCorrect, scoreAnswered, revealDelayMs, onHearIt, onAnswer } = props
  const [revealed, setRevealed] = useState(false)

  // Reset reveal state whenever a new question mounts (score progresses).
  useEffect(() => {
    setRevealed(false)
  }, [scoreAnswered])

  const handleHearIt = (): void => {
    onHearIt()
    if (!revealed) {
      window.setTimeout(() => setRevealed(true), revealDelayMs)
    }
  }

  return (
    <section className={styles.panel} aria-label="Voice-Leading Quiz question">
      <header className={styles.header}>
        <span className={styles.score}>
          {scoreCorrect} / {scoreAnswered} correct
        </span>
      </header>

      <p className={styles.prompt}>Which voicing has the smoother voice leading?</p>

      <Button type="button" variant="contained" onClick={handleHearIt} className={styles.hearItButton}>
        Hear both
      </Button>

      {revealed ? (
        <div className={styles.answers}>
          <Button type="button" variant="outlined" onClick={() => onAnswer('A')}>
            Voicing A
          </Button>
          <Button type="button" variant="outlined" onClick={() => onAnswer('B')}>
            Voicing B
          </Button>
        </div>
      ) : null}
    </section>
  )
}
